/**
 * Credit Balance Widget
 * Displays current credit balance with low balance warnings
 */

import React, { useEffect, useState } from 'react';
import { creditService } from '../services/credit.service';

interface CreditBalanceWidgetProps {
  className?: string;
  showDetails?: boolean;
  lowBalanceThreshold?: number;
}

const CreditBalanceWidget: React.FC<CreditBalanceWidgetProps> = ({
  className = '',
  showDetails = true,
  lowBalanceThreshold = 10000, // 100 INR in paise
}) => {
  const [balance, setBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadBalance();
    
    // Refresh balance every 30 seconds
    const interval = setInterval(loadBalance, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadBalance = async () => {
    try {
      setLoading(true);
      const result = await creditService.getBalance();
      
      if (result.success) {
        setBalance(result.balance!);
        setError(null);
      } else {
        setError(result.error || 'Failed to load balance');
      }
    } catch (err) {
      setError('Failed to load balance');
    } finally {
      setLoading(false);
    }
  };

  const isLowBalance = balance !== null && balance < lowBalanceThreshold;
  const isZeroBalance = balance === 0;

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow p-4 ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
          <div className="h-8 bg-gray-200 rounded w-32"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-lg shadow p-4 ${className}`}>
        <div className="text-red-600">
          <p className="text-sm font-medium">Error loading balance</p>
          <p className="text-xs">{error}</p>
          <button
            onClick={loadBalance}
            className="mt-2 text-xs text-blue-600 hover:text-blue-800"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow p-4 ${className}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">Credit Balance</p>
          <p className={`text-2xl font-bold ${
            isZeroBalance ? 'text-red-600' : 
            isLowBalance ? 'text-yellow-600' : 
            'text-green-600'
          }`}>
            {creditService.formatAmount(balance!)}
          </p>
        </div>
        
        <div className="flex items-center">
          {isZeroBalance ? (
            <div className="flex items-center text-red-600">
              <svg className="w-6 h-6 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-xs font-medium">No Credits</span>
            </div>
          ) : isLowBalance ? (
            <div className="flex items-center text-yellow-600">
              <svg className="w-6 h-6 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-xs font-medium">Low Balance</span>
            </div>
          ) : (
            <div className="flex items-center text-green-600">
              <svg className="w-6 h-6 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-xs font-medium">Good</span>
            </div>
          )}
        </div>
      </div>

      {showDetails && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <button
              onClick={loadBalance}
              className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
            >
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
              </svg>
              Refresh
            </button>
            
            <span className="text-xs text-gray-500">
              Last updated: {new Date().toLocaleTimeString()}
            </span>
          </div>
        </div>
      )}

      {/* Warning Messages */}
      {isZeroBalance && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800 font-medium">
            No credits available
          </p>
          <p className="text-xs text-red-600 mt-1">
            Contact your administrator to load credits before creating batches.
          </p>
        </div>
      )}

      {isLowBalance && !isZeroBalance && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm text-yellow-800 font-medium">
            Low credit balance
          </p>
          <p className="text-xs text-yellow-600 mt-1">
            Consider requesting more credits to avoid service interruption.
          </p>
        </div>
      )}
    </div>
  );
};

export default CreditBalanceWidget;